/** @jsx jsx */
import * as React from "react";
import { jsx } from "@emotion/core";
import { PATIENT_TRANSFORM_FACTORS } from "../constants";
import * as styles from './style';
import { isEmpty } from "lodash";

import * as moment from "moment";
import { WeekView } from "../../../../components/patient/transform/WeekView/WeekView";
import AppUtils from "../../../../utils/AppUtils";
import { IUserFitnessPlanRequestParams, IUserFitnessManagedPlanParams } from "../interface";
import { IPaginationParams } from "../../../FitnessPlan/AllFitnessPlans/interface";
import { IPaginatedMicrocycles, IMicrocycle, IUserFitnessPlan } from "../../../FitnessPlan/FitnessTemplate/interface";
import { FitnessPlanAssignment } from "../../../../components/patient/transform/PlanAssignmentAndIntervention/AssignPlan/FitnessPlanAssignment/FitnessPlanAssignment";
import { connect } from "react-redux";
import { UserFitnessActions } from "../../../../actions/UserFitnessActions";
import { IAppState } from "../../../../models/IAppState";
import { toastNotificationAppearance } from "../../../../utils/AppConstants";
import { STATE_VALUES } from "../../../FitnessPlan/constants";

interface IMapDispatchToProps {
    fetchAllFitnessPlans?: (reqParams: any, paginationParams: IPaginationParams) => Promise<IPaginatedMicrocycles>
    assignFitnessPlan?: (reqParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => Promise<IMicrocycle>;
    setUserFitnessPlan?: (userPlan: any, startDate: string | number, userProfile: any, isPlanPublished: boolean) => void;
    publishUserFitnessPlan: (planId: number) => Promise<any>;
    deleteUserFitnessPlan: (planId: number) => Promise<any>;
}

interface IWeeklyPlanAssignmentAndInterventionProps extends IMapDispatchToProps {
    userId: string;
    coachId: number;
    userProfile: any;
    fetchUserFitnessPlan: (reqParams: IUserFitnessPlanRequestParams) => Promise<IUserFitnessPlan>;
    fetchUserManagedFitnessPlan: (requestParams: IUserFitnessManagedPlanParams) => Promise<IMicrocycle>;
}

const TransformPatientWeeklyPlan: React.FC<IWeeklyPlanAssignmentAndInterventionProps> = (props: IWeeklyPlanAssignmentAndInterventionProps) => {
    const { fetchUserFitnessPlan, fetchAllFitnessPlans, userId, coachId, assignFitnessPlan, fetchUserManagedFitnessPlan, setUserFitnessPlan, userProfile, publishUserFitnessPlan, deleteUserFitnessPlan } = props;

    const today = new Date().valueOf();

    const mondayOfTheWeek = AppUtils.getEpochInUTC(AppUtils.getStartOfTheWeekFromMonday(new Date()));

    const [startDate, setStartDate] = React.useState(mondayOfTheWeek);
    const [endDate, setEndDate] = React.useState(moment(mondayOfTheWeek).add(6, 'days').endOf('day').valueOf());
    const [fitnessPlansList, setFitnessPlansList] = React.useState(null);
    const [isPublishedFitnessPlan, setIsPublishedFitnessPlan] = React.useState(false);
    const [transformFactorsData, setTransformFactorsData] = React.useState(
        {
            [PATIENT_TRANSFORM_FACTORS.FITNESS]: null,
            [PATIENT_TRANSFORM_FACTORS.HABITS]: null,
        }
    );

    React.useEffect(() => {
        setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: null }));
        //setting initial values
        fetchFitnessPlan();
    }, [startDate])


    // fetch user fitness plan for the selected week
    const fetchFitnessPlan = async () => {
        const reqParams = {
            tenantId: 2,
            startDate,
            userId
        }

        let foundPlan = false;
        try {
            const data = await fetchUserFitnessPlan(reqParams);

            //check if user microcycle exists. If not, then check if microcycle is assigned to user
            if (data.microCycleEntry) {
                setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: data }));
                setIsPublishedFitnessPlan(true);
                foundPlan = true;
            }
            else {

                setIsPublishedFitnessPlan(false);
                //check if microcycle is assigned for the selected week to user
                const queryParams = {
                    userId,
                    tenantId: 2,
                    userPlanStartDate: startDate
                }
                let userAssignedPlan: IMicrocycle = null;
                userAssignedPlan = await fetchUserManagedFitnessPlan(queryParams);
                if (!isEmpty(userAssignedPlan)) {
                    foundPlan = true;
                    setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: userAssignedPlan }));
                }
                else {
                    setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: null }));
                }
            }
        }
        catch {
            AppUtils.triggerToastNotification("Failed to get customer plan. Please try again.", toastNotificationAppearance.error);
        }
        if (!foundPlan && !fitnessPlansList) {
            const queryParams = { createdBy: { value: coachId, query: "eq" }, state: { value: STATE_VALUES.LIVE, query: "eq" } };
            const paginationParams = { offset: 0, limit: 1000, sortBy: "createdOn", sortOrder: "DESC" };
            const fitnessPlans = await fetchAllFitnessPlans(queryParams, paginationParams);
            setFitnessPlansList(fitnessPlans);
        }
    }

    const setDates = (startDate: number, endDate: number) => {
        setStartDate(startDate);
        setEndDate(endDate);
    }

    const getDateText = (selectedDate: number) => {
        const value = AppUtils.getDayEpochInfo(selectedDate);
        return (`${value.dateText} ${value.month}`)
    }

    const handlePublishFitnessPlan = (planId: number) => {
        publishUserFitnessPlan(planId).then(response => {
            AppUtils.triggerToastNotification("Plan has been successfully published", toastNotificationAppearance.success);
            fetchFitnessPlan()
        })
    }

    const handleFitnessPlanAssignment = (selectedPlanId: number) => {
        const reqParams: IUserFitnessPlanRequestParams = {
            userPlanStartDate: startDate,
            userId,
            createdBy: coachId
        }

        assignFitnessPlan(reqParams, selectedPlanId).then((response: any) => {
            setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: response }));
        })
    }

    const deleteUserPlan = (selectedPlanId: number) => {
        setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: null }));
        deleteUserFitnessPlan(selectedPlanId)
            .then(res => setTransformFactorsData(prevState => ({ ...prevState, [PATIENT_TRANSFORM_FACTORS.FITNESS]: null })))
            .catch(err => AppUtils.triggerToastNotification("Failed to delete assigned plan.", toastNotificationAppearance.error));
    }

    const isPastWeek = moment(endDate).diff(today) < 0;
    //check number of days between today and start date. If difference is more than 2 days, disable publish button
    const shouldDisableFutureWeekPublish = moment(startDate).diff(today, 'days') > 1;

    return (
        <section css={styles.assignPlanWrapper}>
            <table css={styles.tableOuterWrapper}>
                <thead >
                    <tr css={styles.tableHeader}>
                        <th css={styles.datesRangeLabel}>
                            {`${getDateText(startDate)} - ${getDateText(endDate)}`}
                        </th>
                        <WeekView startDate={startDate} endDate={endDate} setDates={setDates} />
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td colSpan={10}>
                            <hr css={styles.divider} />
                        </td>
                    </tr>
                    {
                        Object.keys(transformFactorsData).map((factor: any) => {
                            return (<tr key={factor[0]}>
                                <td css={styles.factorLabel}>
                                    {factor}
                                </td>
                                {/* taking up arrow space */}
                                <td css={{ width: 1 }}></td>
                                {
                                    factor === PATIENT_TRANSFORM_FACTORS.FITNESS ?
                                        <FitnessPlanAssignment
                                            fitnessPlansList={fitnessPlansList}
                                            assignFitnessPlan={handleFitnessPlanAssignment}
                                            assignedFitnessPlan={transformFactorsData[factor]}
                                            isPastWeek={isPastWeek}
                                            transformFactor={factor}
                                            userId={userId}
                                            startDate={startDate}
                                            userProfile={userProfile}
                                            setUserFitnessPlan={setUserFitnessPlan}
                                            isPublishedFitnessPlan={isPublishedFitnessPlan}
                                            handlePublishFitnessPlan={handlePublishFitnessPlan}
                                            deleteUserFitnessPlan={deleteUserPlan}
                                            disablePublish={(transformFactorsData[factor] && transformFactorsData[factor].state != STATE_VALUES.LIVE) || shouldDisableFutureWeekPublish || isPastWeek} />
                                        :
                                        <td css={styles.plusIcon({ isPastWeek })}><span css={styles.circle({ isPastWeek })}>+</span></td>
                                }
                            </tr>)
                        })
                    }
                </tbody>

            </table>
        </section>
    )
}

const mapStateToProps = (state: IAppState) => ({

});

const mapDispatchToProps = (dispatch: React.Dispatch<any>): IMapDispatchToProps => ({
    fetchAllFitnessPlans: (params: { [key: string]: { value: string | number, query: string } }, paginationParams: IPaginationParams) => UserFitnessActions.fetchAllFitnessPlans(params, paginationParams),
    assignFitnessPlan: (requestParams: IUserFitnessPlanRequestParams, selectedPlanId: number) => UserFitnessActions.assignFitnessPlan(requestParams, selectedPlanId),
    setUserFitnessPlan: (userPlan: any, startDate: string | number, userProfile: any, isPlanPublished: boolean) => UserFitnessActions.setUserFitnessPlan(userPlan, startDate, userProfile, isPlanPublished, dispatch),
    publishUserFitnessPlan: (planId: number) => UserFitnessActions.publishUserFitnessPlan(planId),
    deleteUserFitnessPlan: (planId: number) => UserFitnessActions.deleteUserFitnessPlan(planId)
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TransformPatientWeeklyPlan);
